// Hook to detect first-time users and manage onboarding state (checks backend for onboarding completion).
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { safeLog } from '@/utils/safeLog';

interface OnboardingData {
  notificationsGranted: boolean;
  permissions: Record<string, boolean>;
  completedAt: string;
}

export function useFirstTimeUser() {
  const [isFirstTime, setIsFirstTime] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    
    const checkUser = async () => {
      if (!currentUser) {
        if (isMounted) {
          setIsFirstTime(null);
          setIsLoading(false);
        }
        return;
      }

      if (isChecking) return;

      // Set a timeout to prevent infinite loading
      timeoutId = setTimeout(() => {
        if (isMounted) {
          console.warn('Onboarding check timed out, falling back to localStorage');
          checkLocalStorage();
          setIsLoading(false);
        }
      }, 10000); // 10 second timeout

      try {
        await checkFirstTimeStatus();
      } finally {
        clearTimeout(timeoutId);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const checkLocalStorage = () => {
      try {
        const onboardingData = localStorage.getItem('shieldhaven_onboarding');
        if (onboardingData) {
          const data: OnboardingData = JSON.parse(onboardingData);
          // Check if onboarding was completed within the last 30 days
          const completedAt = new Date(data.completedAt);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          const isRecent = completedAt > thirtyDaysAgo;
          setIsFirstTime(!isRecent);
        } else {
          setIsFirstTime(true);
        }
      } catch (error) {
        safeLog.error('Error checking localStorage:', error);
        // If even localStorage fails, assume first time for safety
        setIsFirstTime(true);
      }
    };

    checkUser();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [currentUser?.id]);

  const checkFirstTimeStatus = async () => {
    if (!currentUser || isChecking) {
      setIsFirstTime(null);
      return;
    }

    setIsChecking(true);

    try {
      // Set a promise timeout for the database call
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Database query timeout')), 8000);
      });
      
      const queryPromise = userService.getUserProfile(currentUser.id);
      
      // Race between the query and timeout
      const { user, error } = await Promise.race([queryPromise, timeoutPromise]) as Awaited<typeof queryPromise>;
      
      if (error) {
        safeLog.error('Error checking user profile:', error);
        
        // If user not found, they're definitely first-time
        if (error.includes('User not found') || error.includes('No rows') || error.includes('PGRST116')) {
          setIsFirstTime(true);
        } else {
          // Other database error, fallback to localStorage
          const onboardingData = localStorage.getItem('shieldhaven_onboarding');
          if (onboardingData) {
            const data: OnboardingData = JSON.parse(onboardingData);
            const completedAt = new Date(data.completedAt);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            setIsFirstTime(completedAt < thirtyDaysAgo);
          } else {
            setIsFirstTime(true);
          }
        }
        return;
      }

      if (user) {
        // User exists in database, check onboarding status
        setIsFirstTime(!user.hasCompletedOnboarding);
      } else {
        // User doesn't exist in database, they're definitely first-time
        setIsFirstTime(true);
      }
    } catch (error) {
      safeLog.error('Exception checking first-time status:', error);
      // Fallback to localStorage on any error (including timeout)
      try {
        const onboardingData = localStorage.getItem('shieldhaven_onboarding');
        if (onboardingData) {
          const data: OnboardingData = JSON.parse(onboardingData);
          const completedAt = new Date(data.completedAt);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          setIsFirstTime(completedAt < thirtyDaysAgo);
        } else {
          setIsFirstTime(true);
        }
      } catch (localStorageError) {
        safeLog.error('Error with localStorage fallback:', localStorageError);
        setIsFirstTime(true);
      }
    } finally {
      setIsChecking(false);
    }
  };

  const markOnboardingComplete = async (data: Omit<OnboardingData, 'completedAt'>) => {
    if (!currentUser) {
      safeLog.error('No current user to update');
      return;
    }

    try {
      // Update database
      const result = await userService.updateOnboardingStatus(currentUser.id, data);
      
      if (result.success) {
        setIsFirstTime(false);
        
        // Also save to localStorage as backup
        saveToLocalStorage(data);
      } else {
        safeLog.error('Failed to update database:', result.error);
        // Fallback to localStorage
        saveToLocalStorage(data);
        // Still mark as complete even if DB update failed
        setIsFirstTime(false);
      }
    } catch (error) {
      safeLog.error('Exception updating onboarding status:', error);
      // Fallback to localStorage
      saveToLocalStorage(data);
      // Still mark as complete even if DB update failed
      setIsFirstTime(false);
    }
  };

  const saveToLocalStorage = (data: Omit<OnboardingData, 'completedAt'>) => {
    try {
      const onboardingData: OnboardingData = {
        ...data,
        completedAt: new Date().toISOString()
      };
      
      localStorage.setItem('shieldhaven_onboarding', JSON.stringify(onboardingData));
      localStorage.setItem('shieldhaven_permissions', JSON.stringify(data.permissions));
      setIsFirstTime(false);
    } catch (error) {
      safeLog.error('Error saving onboarding data to localStorage:', error);
    }
  };

  const resetOnboarding = async () => {
    if (!currentUser) {
      safeLog.error('No current user to reset');
      return;
    }

    try {
      // Reset in database by setting hasCompletedOnboarding to false
      const result = await userService.updateOnboardingStatus(currentUser.id, {
        notificationsGranted: false,
        permissions: {}
      });

      if (result.success) {
        setIsFirstTime(true);
      } else {
        safeLog.error('Failed to reset in database:', result.error);
      }
    } catch (error) {
      safeLog.error('Exception resetting onboarding:', error);
    }

    // Also clear localStorage
    try {
      localStorage.removeItem('shieldhaven_onboarding');
      localStorage.removeItem('shieldhaven_permissions');
      setIsFirstTime(true);
    } catch (error) {
      safeLog.error('Error clearing localStorage:', error);
    }
  };

  const getStoredPermissions = (): Record<string, boolean> => {
    try {
      const permissions = localStorage.getItem('shieldhaven_permissions');
      return permissions ? JSON.parse(permissions) : {};
    } catch (error) {
      safeLog.error('Error getting stored permissions:', error);
      return {};
    }
  };

  return {
    isFirstTime,
    isLoading,
    markOnboardingComplete,
    resetOnboarding,
    getStoredPermissions
  };
}